import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from './ui/card';
import { Check, XCircle, Clock } from 'lucide-react';
import { getPaymentStatus } from '../services/api';
import { trackEvent } from '../services/metaPixel';
import ekoIcon from '../assets/eko-_transparent-removebg-preview.png';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 20; // ~40 seconds

/** Order value PaymentPage parks before handing off to Stripe. */
export const PENDING_PURCHASE_KEY = 'dpp-pending-purchase';
/**
 * One generic product id for every assessment purchase. Meta wants a content
 * id; it must not be a child id, so the same constant covers all of them.
 */
export const PURCHASE_CONTENT_ID = 'child_assessment';
/** Payment ids already reported, so a refresh of this page cannot re-report. */
const REPORTED_PURCHASES_KEY = 'dpp-reported-purchases';

type ResultState = 'verifying' | 'success' | 'failed' | 'timeout';

// Reporting one payment twice would overstate revenue in Ads Manager and skew
// the bidding that optimises against it. The eventID below lets Meta dedupe as
// a second line of defence, but refusing to send is cleaner than relying on it.
const reportPurchase = (paymentId: string) => {
  let reported: string[] = [];
  try {
    reported = JSON.parse(localStorage.getItem(REPORTED_PURCHASES_KEY) || '[]');
  } catch {
    reported = [];
  }
  if (reported.includes(paymentId)) return;

  let pending: { paymentId?: string; valueCents?: number; numItems?: number } = {};
  try {
    pending = JSON.parse(sessionStorage.getItem(PENDING_PURCHASE_KEY) || '{}');
  } catch {
    pending = {};
  }
  // The stash is lost if checkout finishes in a different tab. The conversion
  // still matters more than its value, so send what we have either way.
  const hasValue = pending.paymentId === paymentId && typeof pending.valueCents === 'number';

  // Revenue and profile count only — no child id, name, grade or result.
  trackEvent(
    'Purchase',
    {
      currency: 'USD',
      value: hasValue ? pending.valueCents! / 100 : 0,
      num_items: pending.numItems,
      content_ids: [PURCHASE_CONTENT_ID],
      content_type: 'product',
    },
    { eventID: `purchase-${paymentId}` }
  );

  localStorage.setItem(REPORTED_PURCHASES_KEY, JSON.stringify([...reported, paymentId].slice(-20)));
  sessionStorage.removeItem(PENDING_PURCHASE_KEY);
};

export const PaymentResult: React.FC<{ cancelled?: boolean }> = ({ cancelled = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ResultState>(cancelled ? 'failed' : 'verifying');
  const attempts = useRef(0);

  useEffect(() => {
    if (cancelled) return;
    let cancelledEffect = false;
    let timer: ReturnType<typeof setTimeout>;

    const sessionId = searchParams.get('session_id') || undefined;
    const paymentId = sessionStorage.getItem('dpp-payment-id') || undefined;

    const poll = async () => {
      if (cancelledEffect) return;
      attempts.current += 1;
      try {
        const result = await getPaymentStatus({ paymentId, sessionId });
        if (cancelledEffect) return;
        if (result.status === 'completed') {
          reportPurchase(result.payment_id);
          localStorage.removeItem('phonics-profiles');
          sessionStorage.removeItem('dpp-payment-id');
          setState('success');
          return;
        }
        if (result.status === 'failed' || result.status === 'expired') {
          setState('failed');
          return;
        }
      } catch {
        // keep polling — webhook may not have arrived yet
      }
      if (attempts.current >= MAX_POLL_ATTEMPTS) {
        setState('timeout');
      } else {
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelledEffect = true;
      clearTimeout(timer);
    };
  }, [cancelled, searchParams]);

  const goToDashboard = () => {
    localStorage.removeItem('phonics-profiles');
    navigate('/app', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F3EEE8' }}>
      {/* Decorative background blobs */}
      <div className="fixed -top-10 -left-10 w-72 h-48 bg-gradient-to-r from-[#FFF2E6] to-[#FFF0F6] rounded-full opacity-50 blur-3xl pointer-events-none -z-10" />
      <div className="fixed bottom-0 -right-16 w-72 h-48 bg-gradient-to-r from-[#EDE9FF] to-[#FFF0F6] rounded-full opacity-50 blur-3xl pointer-events-none -z-10" />

      <Card className="p-8 sm:p-10 rounded-3xl bg-white/95 backdrop-blur border-4 shadow-xl max-w-md w-full text-center">
        {state === 'verifying' && (
          <>
            <img src={ekoIcon} alt="Eko mascot" className="w-20 h-20 mx-auto mb-4 animate-gentle-bounce object-contain" />
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#F7B26A] border-t-transparent mb-4"></div>
            <h2 className="text-xl font-bold text-[#1F224B] mb-2">Confirming your payment...</h2>
            <p className="text-sm text-[#6B6FA3]">This usually takes just a few seconds.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <img src={ekoIcon} alt="Eko mascot" className="w-24 h-24 mx-auto mb-4 animate-gentle-float object-contain" />
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F224B] mb-2">You're all set! 🎉</h2>
            <p className="text-sm text-[#6B6FA3] mb-6">
              Your child's profile is unlocked. They can start their assessment right away!
            </p>
            <button
              onClick={goToDashboard}
              className="w-full h-14 rounded-2xl font-bold text-lg text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 transition-all shadow-lg btn-bouncy"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {state === 'failed' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F224B] mb-2">
              {cancelled ? 'Payment cancelled' : 'Payment not completed'}
            </h2>
            <p className="text-sm text-[#6B6FA3] mb-6">
              {cancelled
                ? "No worries — you haven't been charged. You can come back whenever you're ready."
                : "Something went wrong and the payment didn't go through. You haven't been charged."}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/app/payment', { replace: true })}
                className="w-full h-14 rounded-2xl font-bold text-lg text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 transition-all shadow-lg btn-bouncy"
              >
                Try Again
              </button>
              <button
                onClick={goToDashboard}
                className="w-full h-12 rounded-2xl font-semibold text-[#1F224B] bg-gray-100 hover:bg-gray-200 transition-all"
              >
                Back to Dashboard
              </button>
            </div>
          </>
        )}

        {state === 'timeout' && (
          <>
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F224B] mb-2">Payment is processing</h2>
            <p className="text-sm text-[#6B6FA3] mb-6">
              Your payment is taking a little longer to confirm. If you were charged, your child's
              profile will unlock automatically within a few minutes — just refresh the dashboard.
            </p>
            <button
              onClick={goToDashboard}
              className="w-full h-14 rounded-2xl font-bold text-lg text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 transition-all shadow-lg btn-bouncy"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </Card>
    </div>
  );
};

export default PaymentResult;
